import React, { useState, Fragment, useEffect, useContext } from "react";
import { SortableContainer, SortableElement } from "react-sortable-hoc";
import { AppConfigContext } from "../../context/AppConfigContext";
import { DestajosContext } from "../../context/DestajosContext";
import { EditModeContext } from "../../context/EditModeContext";
import { ModalContext } from "../../context/ModalContext";
import BitacoraConcepto from "../conceptos/BitacoraConceptos";
import DestajoTabla from "./DestajoTabla";
import DestajoForm from "./DestajoForm";
import { getValue, formatPartidaDate } from "../../utils";
import Rubro from "../common/Rubro";
import CheckBox from "./CheckBox";
import AvancePickerMobile from "../cronograma/AvancePickerMobile";
import { PartidasContext } from "../../context/PartidasContext";
import { AvanceModeContext } from "../../context/AvanceModeContext";
import { canEditPartidas } from "../../utils/permisos";
import {
  renderNombrePartida,
  partidaTieneAditivos,
} from "../../utils/partidas";
import { compararPorOrden } from "../../utils/orden";

const DestajosContainer = SortableContainer((props) => props.renderDestajos());

const DestajoFila = SortableElement((props) => (
  <DestajoForm
    index={props.index}
    idProyecto={props.idProyecto}
    idPartida={props.idPartida}
    destajo={props.destajo}
    key={"destajo-" + props.destajo.idDestajo}
    setPropiedadDestajo={props.setPropiedadDestajo}
    deleteDestajo={props.deleteDestajo}
    confirm={props.confirm}
  />
));

const PartidaTabla = ({
  partida,
  proyecto,
  destajos,
  subdestajos,
  getProyecto,
  showDetails,
  forceOpen,
  busqueda,
}) => {
  const [showDestajos, setShowDestajos] = useState(false);
  const [destajosPartida, setDestajosPartida] = useState([]);

  const { idProyecto, permiso, idPermiso } = proyecto;

  // Partidas only take avance when the project tracks progress at the partida level.
  const showAvancePicker = (proyecto.modo_avance || "destajo") === "partida";

  console.log("PartidaTabla permiso:", permiso, "idPermiso:", idPermiso);

  const {
    postDestajos,
    deleteDestajo,
    reordenarDestajos,
    createNuevoDestajo,
    setPropertyDestajo,
  } = useContext(DestajosContext);

  const appConfig = useContext(AppConfigContext);
  const { postAvancePartida } = useContext(PartidasContext);
  const { confirm, modalComponent } = useContext(ModalContext);
  const { inputType } = useContext(AvanceModeContext);

  const { level, editMode, viewLevel, editModeOn } =
    useContext(EditModeContext);


  useEffect(() => {
    if (Array.isArray(destajos)) {
      handleDestajosPartida();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destajos]);

  // Opened/closed by the search bar (undefined when there is no search).
  useEffect(() => {
    setShowDestajos(Boolean(forceOpen));
  }, [forceOpen]);

  function canEditProgress() {
    return (
      ["admin", "supervisor", "contratista"].includes(permiso) ||
      [3, 6].includes(parseInt(idPermiso))
    );
  }

  function handleDestajosPartida() {
    let current = destajos
      .filter((destajo) => destajo.idPartida === partida.idPartida)
      .sort((a, b) => compararPorOrden(a, b, "idDestajo"));
    setDestajosPartida(current);
  }

  // Comments of the partida and of everything under it (destajos,
  // subdestajos and conceptos).
  function handleComments() {
    modalComponent(
      `Comentarios de ${partida.nombre}`,
      <BitacoraConcepto idPartida={partida.idPartida} onChange={getProyecto} />
    );
  }

  function toggleDestajos() {
    setShowDestajos(!showDestajos);
    if (!showDestajos) viewLevel("destajos");
    else viewLevel("partidas");
  }

  function handleReorder(props) {
    reordenarDestajos(partida.idPartida, props);
  }

  function handleCreateDestajo() {
    createNuevoDestajo(partida.idPartida, destajosPartida.length);
  }

  function handlePostDestajos() {
    postDestajos(idProyecto, destajosPartida, getProyecto);
  }

  function postAvance(cont, isManual) {
    postAvancePartida(
      partida.idPartida,
      cont,
      idProyecto,
      getProyecto,
      isManual
    );
  }

  function renderDestajos() {
    if (showDestajos) {
      let destajos = destajosPartida.filter(
        (destajo) => String(destajo.idDestajo)[0] !== "d"
      );
      if (editMode === "part-" + partida.idPartida)
        return destajos.map((destajo, index) => {
          return (
            <DestajoFila
              index={index}
              confirm={confirm}
              destajo={destajo}
              idProyecto={idProyecto}
              idPartida={partida.idPartida}
              deleteDestajo={deleteDestajo}
              key={"destajo-" + destajo.idDestajo}
              setPropiedadDestajo={setPropertyDestajo}
            />
          );
        });
      // Searching: only the matching destajos, unless the partida itself matches.
      if (busqueda && !busqueda.partidasCoinciden.has(partida.idPartida)) {
        destajos = destajos.filter(({ idDestajo }) =>
          busqueda.destajosVisibles.has(idDestajo)
        );
      }
      return destajos.map((destajo) => {
          return (
            <DestajoTabla
              key={"destajo-" + destajo.idDestajo}
              idPartida={partida.idPartida}
              subdestajos={subdestajos}
              getProyecto={getProyecto}
              idProyecto={idProyecto}
              editModeOn={editModeOn}
              viewLevel={viewLevel}
              editMode={editMode}
              confirm={confirm}
              permiso={permiso}
              destajo={destajo}
              level={level}
              forceOpen={
                busqueda ? busqueda.openDestajos.has(destajo.idDestajo) : undefined
              }
              busqueda={busqueda}
            />
          );
        });
    }
  }

  // Sum of the destajos' ponderaciones being edited (deleted ones excluded).
  const renderSumaPonderaciones = () => {
    const suma = destajosPartida
      .filter((destajo) => String(destajo.idDestajo)[0] !== "d")
      .reduce((total, { ponderacion }) => total + (parseFloat(ponderacion) || 0), 0);
    const redondeada = Math.round(suma * 100) / 100;
    const completa = redondeada === 100;
    return (
      <div
        className={`mb-2 bold ${completa ? "text-success" : "text-muted"}`}
        title="Suma de las ponderaciones de los conceptos de la partida"
      >
        <i className={`fa ${completa ? "fa-check-circle" : "fa-balance-scale"} me-2`} />
        Ponderación: {redondeada} / 100%
      </div>
    );
  };

  function renderEditbutton() {
    let components = [];
    if (showDestajos && canEditPartidas(permiso)) {
      if (editMode === "part-" + partida.idPartida)
        components = [
          <button
            className="btn btn-primary me-2"
            onClick={handlePostDestajos}
            key={"save-destajos-" + partida.idPartida + "-btn"}
          >
            Guardar
          </button>,
          <button
            className="btn btn-outline-primary"
            key={"add-destajos-" + partida.idPartida + "-btn"}
            onClick={handleCreateDestajo}
          >
            <i className="fa fa-plus me-2"></i>
            Agregar Destajo
          </button>,
          <button
            key={"cancelPartida-" + partida.idPartida}
            className="btn btn-outline-danger ms-2"
            onClick={() =>
              confirm(
                "¿Está seguro que desea cancelar? Los cambios a los Destajos de la Partida " +
                  partida.nombre +
                  " no se guardarán.",
                getProyecto
              )
            }
          >
            Cancelar
          </button>,
        ];
      else if (!editMode) {
        components = [
          <button
            className="btn btn-outline-primary"
            onClick={() => editModeOn(`part-${partida.idPartida}`)}
            key={"edit-destajos-" + partida.idPartida + "-btn"}
          >
            <i className="fa fa-edit me-2"></i>
            Editar Conceptos de {partida.nombre}
          </button>,
        ];
      }
      return (
        <tr className="border-primary border-bottom bg-primary-light">
          <td />
          <td className="ps-2" style={{ minWidth: 330 }} colSpan={6}>
            {editMode === "part-" + partida.idPartida &&
              renderSumaPonderaciones()}
            {components}
          </td>
        </tr>
      );
    }
  }

  const renderProgress = () => {
    let progreso = partida.real * 100;
    if (getValue(appConfig, "progress") === "money") {
      progreso = parseInt(partida.progreso);
    } else if (canEditProgress() && showAvancePicker) {
      const generalPartida = parseFloat(partida.generalPartida);
      const realPct = Math.round((parseFloat(partida.real) || 0) * 100);
      const pickerValue = realPct;
        /*!isNaN(generalPartida) && generalPartida > 0
          ? Math.round(generalPartida)
          : realPct;*/
      return (
        <AvancePickerMobile
          permitirExcedente
          progreso={pickerValue}
          setCantidad={postAvance}
          intervalo={1}
          limite={100}
          inputType={inputType}
        />
      );
    }
    if (isNaN(parseInt(progreso))) return "-";
    return `${parseInt(progreso)}%`;
  };

  // Open partidas are highlighted; partidas with aditivos are yellow.
  const getRowClassName = () => {
    const classes = ["align-items-center"];
    if (showDestajos) {
      classes.push("bg-primary-light border-top border-primary");
    } else {
      classes.push("border-bottom");
    }
    if (partidaTieneAditivos(partida, destajos, subdestajos)) {
      classes.push("concepto-aditivo");
    }
    return classes.join(" ");
  };

  let { nombre, ponderacion, punto_critico, real } = partida;
  const fecha_inicio = getValue(partida, "fecha_inicio");
  const fecha_fin = getValue(partida, "fecha_fin");
  return (
    <Fragment>
      <tr
        className={getRowClassName()}
      >
        <td className="ponderacion">
          <p className="h5 mb-0 bold">
            {ponderacion !== null && !isNaN(parseFloat(ponderacion))
              ? parseFloat(ponderacion)
              : "-"}
            %
          </p>
        </td>
        <td className="rubro">
          <div className="row mx-0">
            <div className="col-2 px-0">
              <div
                className={`btn btn-sm btn-${
                  showDestajos ? "primary" : "light"
                } border`}
                onClick={toggleDestajos}
              >
                <i
                  className={`fas fa-chevron-${
                    showDestajos ? "up" : "down"
                  } toggle`}
                />
              </div>
            </div>
            <div className="col-10 px-0">
              <Rubro
                completado={real === 1}
                nombre={nombre ? renderNombrePartida(partida) : nombre}
                className="bold h6"
              />
            </div>
          </div>
        </td>
        <td
          className="text-center bold h5"
          style={{ width: 180, minWidth: 180 }}
        >
          {renderProgress()}
        </td>
        <td className="text-center">
          <CheckBox checked={punto_critico} disabled />
        </td>
        <td>
          {fecha_inicio ? formatPartidaDate(fecha_inicio, "DD MMM YYYY") : "-"}
        </td>
        <td>{fecha_fin ? formatPartidaDate(fecha_fin, "DD MMM YYYY") : "-"}</td>
        <td className="buttons">
          <button
            className={`btn btn-sm btn-outline-${
              partida.comentarios > 0 ? "primary" : "secondary"
            } me-2`}
            onClick={handleComments}
            title="Comentarios"
          >
            <i className="fa fa-comment"></i>
            {partida.comentarios > 0 ? ` ${partida.comentarios}` : ""}
          </button>
          <button
            className="btn btn-sm btn-light border"
            onClick={() => showDetails(partida)}
          >
            <i className="fa fa-info mx-1"></i>
          </button>
        </td>
      </tr>
      {showDestajos && (
        <DestajosContainer
          renderDestajos={renderDestajos}
          toggleDestajos={toggleDestajos}
          onSortEnd={handleReorder}
          showDestajos={showDestajos}
          pressDelay={100}
          useDragHandle
        />
      )}
      {renderEditbutton()}
    </Fragment>
  );
};

export default PartidaTabla;
